import { authClient } from "@/lib/auth-client"
import { useAppStore } from "@/store"
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "@preview-kit/ui/components/avatar"
import { Button } from "@preview-kit/ui/components/button"
import { Separator } from "@preview-kit/ui/components/separator"
import { cn } from "@preview-kit/ui/lib/utils"
import { AnimatePresence, motion } from "framer-motion"
import {
	Globe,
	Inbox,
	Loader,
	Menu,
	MessageCirclePlus,
	Share,
} from "lucide-react"
import type React from "react"
import { useEffect, useRef, useState } from "react"
import { useShallow } from "zustand/shallow"
import CommandListView from "../command-menu/command-list"
import { CommandMenu } from "../command-menu/command-menu"
import OpenGraphView from "../command-menu/views/opengraph-view"
import "./index.css"

interface Position {
	x: number
	y: number
}

export function ActionBar() {
	const {
		isAnimated,
		commandOpen,
		isClickAnimating,
		toggleCommentsOpen,
		toggleCommandOpen,
		setIsCommentMode,
		setCommandOpen,
		setNavigation,
	} = useAppStore(
		useShallow((state) => ({
			commandOpen: state.commandOpen,
			isAnimated: state.isAnimated,
			isClickAnimating: state.isClickAnimating,
			toggleCommentsOpen: state.toggleCommentsOpen,
			toggleCommandOpen: state.toggleCommandOpen,
			setIsCommentMode: state.setIsCommentMode,
			setCommandOpen: state.setCommandOpen,
			setNavigation: state.setNavigation,
		})),
	)
	const {
		data: session,
		isPending, //loading state
		refetch,
	} = authClient.useSession()

	// Refetch session on window focus (refetchOnWindowFocus behavior)
	useEffect(() => {
		const handleFocus = () => {
			refetch()
		}
		window.addEventListener("focus", handleFocus)
		return () => window.removeEventListener("focus", handleFocus)
	}, [refetch])
	// Additionally refetch when tab becomes visible again (covers cases where focus event may not fire)
	useEffect(() => {
		const handleVisibility = () => {
			if (document.visibilityState === "visible") {
				refetch()
			}
		}
		document.addEventListener("visibilitychange", handleVisibility)
		return () =>
			document.removeEventListener("visibilitychange", handleVisibility)
	}, [refetch])

	const [position, setPosition] = useState<Position>(() => {
		// Try to load saved position from localStorage first
		if (typeof window !== "undefined") {
			try {
				const savedPosition = localStorage.getItem("widget-position")
				if (savedPosition) {
					const parsed = JSON.parse(savedPosition)
					if (parsed.x >= 0 && parsed.y >= 0) {
						return parsed
					}
				}
			} catch {
				console.warn("Failed to load saved widget position")
			}

			// Fallback to default centered position if no saved position or invalid
			const viewportWidth = window.innerWidth
			const viewportHeight = window.innerHeight
			const estimatedWidth = 350
			const estimatedHeight = 60
			return {
				x: (viewportWidth - estimatedWidth) / 2,
				y: viewportHeight - estimatedHeight - 20,
			}
		}
		return { x: 0, y: 0 }
	})
	const [isDragging, setIsDragging] = useState(false)
	const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 })
	const buttonRef = useRef<HTMLDivElement>(null)

	// Save position to localStorage whenever it changes
	useEffect(() => {
		try {
			localStorage.setItem("widget-position", JSON.stringify(position))
		} catch {
			console.warn("Failed to save widget position")
		}
	}, [position])

	const handleMouseDown = (e: React.MouseEvent) => {
		if (!buttonRef.current) return

		// Prevent dragging if clicking on interactive elements
		const target = e.target as HTMLElement
		if (target.closest("button") || target.closest("[data-no-drag]")) {
			return
		}

		const rect = buttonRef.current.getBoundingClientRect()
		setDragOffset({
			x: e.clientX - rect.left,
			y: e.clientY - rect.top,
		})
		setIsDragging(true)
		e.preventDefault()
	}

	const handleTouchStart = (e: React.TouchEvent) => {
		if (!buttonRef.current) return

		// Prevent dragging if touching interactive elements
		const target = e.target as HTMLElement
		if (target.closest("button") || target.closest("[data-no-drag]")) {
			return
		}

		const rect = buttonRef.current.getBoundingClientRect()
		const touch = e.touches[0]
		setDragOffset({
			x: touch.clientX - rect.left,
			y: touch.clientY - rect.top,
		})
		setIsDragging(true)
		e.preventDefault()
	}

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!isDragging) return

			const newX = e.clientX - dragOffset.x
			const newY = e.clientY - dragOffset.y

			// Keep button within viewport bounds
			const maxX =
				window.innerWidth - (buttonRef.current?.offsetWidth || 0) - 30
			const maxY =
				window.innerHeight - (buttonRef.current?.offsetHeight || 0) - 20

			setPosition({
				x: Math.max(0, Math.min(newX, maxX)),
				y: Math.max(-435, Math.min(newY, maxY)),
			})
			e.preventDefault()
		}

		const handleTouchMove = (e: TouchEvent) => {
			if (!isDragging) return

			const touch = e.touches[0]
			const newX = touch.clientX - dragOffset.x
			const newY = touch.clientY - dragOffset.y

			// Keep button within viewport bounds
			const maxX =
				window.innerWidth - (buttonRef.current?.offsetWidth || 0) - 30
			const maxY =
				window.innerHeight - (buttonRef.current?.offsetHeight || 0) - 20

			setPosition({
				x: Math.max(0, Math.min(newX, maxX)),
				y: Math.max(0, Math.min(newY, maxY)),
			})
			e.preventDefault()
		}

		const snapToNearestEdge = () => {
			if (!buttonRef.current) return

			const buttonHeight = buttonRef.current.offsetHeight
			const viewportHeight = window.innerHeight

			const newX = position.x
			const newY = viewportHeight - buttonHeight - 20

			// Snap to bottom edge only
			setPosition({ x: newX, y: newY })
		}

		const handleMouseUp = () => {
			if (isDragging) {
				snapToNearestEdge()
			}
			setIsDragging(false)
		}

		const handleTouchEnd = () => {
			if (isDragging) {
				snapToNearestEdge()
			}
			setIsDragging(false)
		}

		if (isDragging) {
			document.addEventListener("mousemove", handleMouseMove)
			document.addEventListener("mouseup", handleMouseUp)
			document.addEventListener("touchmove", handleTouchMove, {
				passive: false,
			})
			document.addEventListener("touchend", handleTouchEnd)
		}

		return () => {
			document.removeEventListener("mousemove", handleMouseMove)
			document.removeEventListener("mouseup", handleMouseUp)
			document.removeEventListener("touchmove", handleTouchMove)
			document.removeEventListener("touchend", handleTouchEnd)
		}
	}, [isDragging, dragOffset, position.x, position.y])

	// Handle window resize to keep button in bounds and snap to nearest edge
	useEffect(() => {
		const handleResize = () => {
			if (!buttonRef.current) return

			const buttonWidth = buttonRef.current.offsetWidth
			const buttonHeight = buttonRef.current.offsetHeight
			const viewportWidth = window.innerWidth
			const viewportHeight = window.innerHeight

			setPosition((prev) => {
				// First ensure button stays in bounds
				const maxX = viewportWidth - buttonWidth
				const boundedX = Math.max(0, Math.min(prev.x, maxX))

				const newX = boundedX
				const newY = viewportHeight - buttonHeight - 20

				// Snap to bottom edge only
				return { x: newX, y: newY }
			})
		}

		window.addEventListener("resize", handleResize)
		return () => window.removeEventListener("resize", handleResize)
	}, [])

	// Handle click outside to close command panel
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const shadowHost = document.getElementsByTagName("preview-kit")[0]

			let isInShadow = false

			if (event.composedPath) {
				isInShadow = event.composedPath().some((node) => node === shadowHost)
			}

			if (
				commandOpen &&
				buttonRef.current &&
				!buttonRef.current.contains(event.target as Node) &&
				!isInShadow
			) {
				setCommandOpen(false)
			}
		}

		if (commandOpen) {
			document?.addEventListener("mousedown", handleClickOutside)
		}

		return () => {
			document?.removeEventListener("mousedown", handleClickOutside)
		}
	}, [commandOpen, setCommandOpen])

	return (
		<div
			ref={buttonRef}
			className={cn(
				`fixed flex justify-center items-end  z-50 select-none w-[520px] h-[500px] transition-all duration-500 ease-out origin-center ${
					isDragging ? "transition-none " : ""
				}`,

				commandOpen ? "z-500" : "pointer-events-none",
			)}
			style={{
				left: `${position.x}px`,
				top: `${position.y}px`,
				touchAction: "none",
			}}
			role="toolbar"
			aria-label="PreviewKit actions"
		>
			<div
				onMouseDown={handleMouseDown}
				onTouchStart={handleTouchStart}
				className={cn(
					"inner_widget",
					"widget_button",
					isDragging
						? "transition-none cursor-grabbing"
						: !commandOpen
							? " cursor-grab"
							: "",
					"p-2 duration-150 flex justify-center items-end overflow-hidden  pointer-events-auto",
					!commandOpen ? "rounded-4xl" : "rounded-xl",
				)}
				style={{
					width:
						isAnimated && !commandOpen
							? "315px"
							: commandOpen
								? "520px"
								: "10px",
					opacity: isAnimated ? 1 : 0,

					height:
						isAnimated && commandOpen ? "500px" : !isAnimated ? "5px" : "50px",
					transform: isClickAnimating ? "scale(0.99)" : "scale(1)",
					transition: isClickAnimating
						? "transform 0.08s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
						: `width 0.6s cubic-bezier(0.16, 1, 0.3, 1), 
						height 0.6s cubic-bezier(0.16, 1, 0.3, 1),
						border-radius 0.6s cubic-bezier(0.16, 1, 0.3, 1),
						transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)`,
				}}
			>
				<div
					className="flex gap-3 items-center text-white"
					style={{
						opacity: isAnimated ? 1 : 0,
						transition: "opacity 0.4s ease-out 0.3s",
					}}
				>
					{isAnimated && (
						<>
							<div className="flex items-center gap-1">
								<AnimatePresence>
									<motion.button
										onClick={() => setIsCommentMode(true)}
										className="rounded-full hover:bg-[#ffffff1a] p-2 transition-colors cursor-pointer"
										data-no-drag
										aria-label="Enter commenting mode"
										title="Enter commenting mode"
										initial={{ opacity: 1, y: 0, filter: "blur(0px)" }}
										animate={
											commandOpen
												? {
														opacity: 0,
														y: 20,
														filter: "blur(8px)",
														transition: {
															duration: 0.2,
															delay: 0.05,
														},
													}
												: {
														opacity: 1,
														y: 0,
														filter: "blur(0px)",
														transition: {
															duration: 0.2,
															delay: 0.26,
														},
													}
										}
									>
										<MessageCirclePlus className="w-4 h-4 text-neutral-600" />
									</motion.button>
								</AnimatePresence>
								<AnimatePresence>
									<motion.button
										onClick={toggleCommentsOpen}
										className="rounded-full hover:bg-[#ffffff1a] p-2 transition-colors cursor-pointer relative"
										data-no-drag
										aria-label="Open comments inbox"
										title="Open comments inbox"
										initial={{ opacity: 1, y: 0, filter: "blur(0px)" }}
										animate={
											commandOpen
												? {
														opacity: 0,
														y: 20,
														filter: "blur(4px)",
														transition: {
															duration: 0.2,
															delay: 0.08,
														},
													}
												: {
														opacity: 1,
														y: 0,
														filter: "blur(0px)",
														transition: {
															duration: 0.2,
															delay: 0.23,
														},
													}
										}
									>
										<Inbox className="w-4 h-4 text-neutral-600" />
										<div
											className="absolute top-1 right-1 w-[6px] h-[6px] bg-red-500 rounded-full"
											aria-hidden="true"
										></div>
									</motion.button>
								</AnimatePresence>
								<AnimatePresence>
									<motion.button
										className="rounded-full hover:bg-[#ffffff1a] p-2 transition-colors cursor-pointer"
										data-no-drag
										onClick={() => {
											setCommandOpen(true)
											setNavigation({
												currentView: <OpenGraphView />,
												breadcrumb: [],
												viewStack: [],
											})
										}}
										aria-label="Share preview"
										title="Share preview"
										initial={{ opacity: 1, y: 0, filter: "blur(0px)" }}
										animate={
											commandOpen
												? {
														opacity: 0,
														y: 20,
														filter: "blur(4px)",
														transition: {
															duration: 0.2,
															delay: 0.11,
														},
													}
												: {
														opacity: 1,
														y: 0,
														filter: "blur(0px)",
														transition: {
															duration: 0.2,
															delay: 0.2,
														},
													}
										}
									>
										<Globe className="w-4 h-4 text-neutral-600" />
									</motion.button>
								</AnimatePresence>
							</div>
							<AnimatePresence>
								<motion.div
									initial={{ opacity: 1, y: 0, filter: "blur(0px)" }}
									animate={
										commandOpen
											? {
													opacity: 0,
													y: 20,
													filter: "blur(4px)",
													transition: {
														duration: 0.2,
														delay: 0.14,
													},
												}
											: {
													opacity: 1,
													y: 0,
													filter: "blur(0px)",
													transition: {
														duration: 0.2,
														delay: 0.17,
													},
												}
									}
								>
									<Separator
										orientation="vertical"
										className="!h-6 bg-neutral-300"
									/>
								</motion.div>
							</AnimatePresence>
						</>
					)}

					<AnimatePresence>
						<motion.div
							initial={{ opacity: 1, y: 0, filter: "blur(0px)" }}
							// className="min-w-[50px] flex justify-center"
							animate={
								commandOpen
									? {
											opacity: 0,
											y: 20,
											filter: "blur(4px)",
											transition: {
												duration: 0.2,
												delay: 0.17,
											},
										}
									: {
											opacity: 1,
											y: 0,
											filter: "blur(0px)",
											transition: {
												duration: 0.2,
												delay: 0.14,
											},
										}
							}
						>
							{session ? (
								<Avatar className="w-6 h-6">
									<AvatarImage src={session.user.image || ""} />
									<AvatarFallback>{session.user.name.charAt(0)}</AvatarFallback>
								</Avatar>
							) : (
								<a
									href={
										"http://localhost:3000/auth/login?next=" +
										encodeURIComponent(window.location.href)
									}
									target="_blank"
									rel="noopener noreferrer"
								>
									<Button
										variant={"outline"}
										size={"sm"}
										className="rounded-4xl text-black "
									>
										{isPending ? (
											<Loader className="animate-spin" />
										) : (
											"Sign in"
										)}
									</Button>
								</a>
							)}
						</motion.div>
					</AnimatePresence>

					<AnimatePresence>
						<motion.div
							initial={{ opacity: 1, y: 0, filter: "blur(0px)" }}
							animate={
								commandOpen
									? {
											opacity: 0,
											y: 20,
											filter: "blur(4px)",
											transition: {
												duration: 0.2,
												delay: 0.2,
											},
										}
									: {
											opacity: 1,
											y: 0,
											filter: "blur(0px)",
											transition: {
												duration: 0.2,
												delay: 0.11,
											},
										}
							}
						>
							<Separator
								orientation="vertical"
								className="!h-6 !bg-neutral-300"
							/>
						</motion.div>
					</AnimatePresence>
					<div className="flex items-center">
						<AnimatePresence>
							<motion.button
								className="rounded-full hover:bg-[#ffffff1a] p-2 transition-colors cursor-pointer "
								data-no-drag
								aria-label="Share options"
								title="Share options"
								initial={{ opacity: 1, y: 0, filter: "blur(0px)" }}
								animate={
									commandOpen
										? {
												opacity: 0,
												y: 20,
												filter: "blur(4px)",
												transition: {
													duration: 0.2,
													delay: 0.23,
												},
											}
										: {
												opacity: 1,
												y: 0,
												filter: "blur(0px)",
												transition: {
													duration: 0.2,
													delay: 0.08,
												},
											}
								}
							>
								<Share className="w-4 h-4 text-neutral-600" />
							</motion.button>
						</AnimatePresence>
						<AnimatePresence>
							<motion.button
								key="menu-btn"
								className="rounded-full hover:bg-[#ffffff1a] p-2 transition-colors cursor-pointer"
								data-no-drag
								onClick={() => {
									toggleCommandOpen()
									setNavigation({
										currentView: <CommandListView />,
										breadcrumb: [],
										viewStack: [],
									})
								}}
								aria-label="Open command menu"
								title="Open command menu"
								initial={{ opacity: 1, y: 0, filter: "blur(0px)" }}
								animate={
									commandOpen
										? {
												opacity: 0,
												y: 20,
												filter: "blur(4px)",
												transition: {
													duration: 0.2,
													delay: 0.26,
												},
											}
										: {
												opacity: 1,
												y: 0,
												filter: "blur(0px)",
												transition: {
													duration: 0.2,
													delay: 0.05,
												},
											}
								}
							>
								<Menu className="w-4 h-4 text-neutral-600" />
							</motion.button>
						</AnimatePresence>
					</div>
				</div>

				{/* Command Menu */}
				<CommandMenu />
			</div>
		</div>
	)
}
